"use client";

import { useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { type Locale } from "@/i18n/routing";
import { savePreferredLocale } from "@/i18n/preferred-locale";
import { getSelectableLanguages } from "@/i18n/languages";
import { Check } from "lucide-react";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { updateUserProfile } from "@/app/actions/auth";
import "flag-icons/css/flag-icons.min.css";

interface LanguageDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LanguageDrawer({ open, onOpenChange }: LanguageDrawerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("profile");
  const [isPending, startTransition] = useTransition();

  const handleLanguageChange = (newLocale: string) => {
    if (newLocale === locale) return;

    // DB에 선호 언어 저장 (fire-and-forget)
    updateUserProfile({ preferred_language: newLocale });
    // 비로그인도 커버되도록 브라우저에도 저장 (랜딩 리다이렉트 + 언어 제안 배너 억제)
    savePreferredLocale(newLocale as Locale);

    // Preserve current query parameters — useSearchParams 훅 대신 클릭 시점에
    // window 에서 읽는다. layout(Navbar)에 마운트되는 컴포넌트가 훅을 쓰면 정적 렌더
    // 페이지에서 Suspense 경계까지 CSR bailout 이 생긴다 (LanguageSuggestBanner 와 동일).
    const query: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      query[key] = value;
    });

    // locale 이 바뀌면 기본 통화도 바뀐다 (사용자가 따로 고른 통화가 있으면 그대로 유지)
    startTransition(() => {
      const href =
        Object.keys(query).length > 0 ? { pathname, query } : pathname;
      router.replace(href, { locale: newLocale as Locale });
    });
    onOpenChange(false);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("menu.language")}
      drawerHeight="fit"
    >
      <div className="pb-8 sm:pb-4">
        <div className="space-y-1">
          {getSelectableLanguages(locale).map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              disabled={isPending}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <span
                  className={`fi fi-${lang.flagCode} w-6! h-auto bg-cover! border border-gray-300`}
                />
                <span className="text-gray-900">{lang.nativeName}</span>
              </div>
              {locale === lang.code && (
                <Check className="w-5 h-5 text-orange-600" />
              )}
            </button>
          ))}
        </div>
      </div>
    </ResponsiveModal>
  );
}
